import {
  VALVE_PRESSURE_UNITS,
  finiteOrNull,
  pressureFromBar,
  pressureToBar,
  pressureUnitDefinition,
  standardAtPressure,
} from './safety-valve-calibration-profile';

const SQRT3 = Math.sqrt(3);
const SQRT12 = Math.sqrt(12);

export { VALVE_PRESSURE_UNITS, finiteOrNull };

export const DEFAULT_CHECK_VALVE_DATA = {
  profile_key: 'check_nonreturn',
  method: 'Verificación funcional del paso directo y de la hermeticidad frente a presión en sentido inverso.',
  procedure: 'Método interno basado en la planilla histórica R-DT-PC-XXXX-XX, revisión 01.',
  pressure_unit: 'bar',
  operating_pressure: null,
  test_pressure: null,
  // Tolerancia sobre la presión de ENSAYO (QA 2026-09-06). Antes la regla exigía
  // que cada intento alcanzara el objetivo exacto, sin margen: una válvula sana
  // ensayada a 5,95 bar contra un objetivo de 6 bar quedaba "no conforme" por
  // 0,05 bar. Es una verificación de que el ensayo se hizo a presión suficiente,
  // no una medición de la válvula, así que admite una tolerancia declarada.
  // Valor por defecto a ratificar por el laboratorio.
  test_pressure_tolerance_percent: 2,
  target_pressure_bar: null,
  initial_pressure: null,
  final_pressure: null,
  working_temperature_c: null,
  service_fluid: '',
  test_fluid: 'Aire',
  inlet_size: '',
  outlet_size: '',
  connection_type: '',
  flow_direction: 'Entrada → salida',
  repeat_count: 1,
  hold_time_seconds: 60,
  forward_flow_result: 'not_performed',
  leak_acceptance: 'Sin fuga visible en sentido inverso',
  decision_rule: 'simple',
  inspection: {
    label_status: 'not_checked',
    seal_status: 'not_checked',
    external_status: 'not_checked',
    internal_status: 'not_checked',
  },
  procedures: {
    cleaning: false,
    seating: false,
    painting: false,
    tests: true,
    recalibration: false,
    sealing: false,
    photographs: false,
  },
  uncertainty_budget: { other_percent: 0 },
  environment_readings: [
    { key: 'start', label: 'Inicio', temp_c: null, pressure_hpa: null, humidity_rh: null },
    { key: 'middle', label: 'Durante', temp_c: null, pressure_hpa: null, humidity_rh: null },
    { key: 'end', label: 'Fin', temp_c: null, pressure_hpa: null, humidity_rh: null },
  ],
  standard_configuration: {},
  result_summary: null,
};

function mean(values = []) {
  const valid = values.map(finiteOrNull).filter((value) => value !== null);
  return valid.length ? valid.reduce((sum, value) => sum + value, 0) / valid.length : null;
}

function sampleStandardDeviation(values = []) {
  const valid = values.map(finiteOrNull).filter((value) => value !== null);
  if (valid.length < 2) return 0;
  const average = mean(valid);
  return Math.sqrt(valid.reduce((sum, value) => sum + ((value - average) ** 2), 0) / (valid.length - 1));
}

export function inferCheckValveProfile(instrument = {}) {
  return {
    code: 'VALVE:check_nonreturn:v1',
    revision: 'v1',
    title: 'Válvula antirretorno',
    unit: pressureUnitDefinition(instrument.unit).key || 'bar',
  };
}

export function mergeCheckValveData(value = {}) {
  const input = value && typeof value === 'object' ? value : {};
  const environment = Array.isArray(input.environment_readings) ? input.environment_readings : DEFAULT_CHECK_VALVE_DATA.environment_readings;
  const pressureUnit = pressureUnitDefinition(input.pressure_unit).key;
  const testPressure = finiteOrNull(input.test_pressure);
  return {
    ...DEFAULT_CHECK_VALVE_DATA,
    ...input,
    profile_key: 'check_nonreturn',
    pressure_unit: pressureUnit,
    test_pressure: testPressure,
    test_pressure_tolerance_percent: Math.max(0, Math.abs(
      finiteOrNull(input.test_pressure_tolerance_percent)
        ?? DEFAULT_CHECK_VALVE_DATA.test_pressure_tolerance_percent,
    )),
    target_pressure_bar: pressureToBar(testPressure, pressureUnit, 'pressure'),
    repeat_count: Math.min(5, Math.max(1, Number(input.repeat_count) || 1)),
    inspection: { ...DEFAULT_CHECK_VALVE_DATA.inspection, ...(input.inspection || {}) },
    procedures: { ...DEFAULT_CHECK_VALVE_DATA.procedures, ...(input.procedures || {}) },
    uncertainty_budget: { ...DEFAULT_CHECK_VALVE_DATA.uncertainty_budget, ...(input.uncertainty_budget || {}) },
    environment_readings: DEFAULT_CHECK_VALVE_DATA.environment_readings.map((fallback, index) => ({ ...fallback, ...(environment[index] || {}) })),
    standard_configuration: input.standard_configuration || {},
    result_summary: input.result_summary || null,
  };
}

export function checkValveBlock() {
  return {
    block_key: 'check_valve_functional',
    display_name: 'Paso directo y hermeticidad inversa',
    unit: 'varios',
    applicable: true,
    point_count: 2,
    sort_order: 1,
    mode: 'functional',
  };
}

function correctedPressure(value, familyData, snapshot) {
  const rawBar = pressureToBar(value, familyData.pressure_unit, 'pressure');
  const standard = standardAtPressure(snapshot, rawBar);
  if (rawBar === null || !standard) return null;
  const correctedBar = rawBar + standard.correction_bar;
  return {
    raw: finiteOrNull(value),
    raw_bar: rawBar,
    correction_bar: standard.correction_bar,
    correction: pressureFromBar(standard.correction_bar, familyData.pressure_unit, 'pressure'),
    corrected_bar: correctedBar,
    corrected: pressureFromBar(correctedBar, familyData.pressure_unit, 'pressure'),
    standard,
  };
}

function uncertainty(corrected, familyData) {
  if (!corrected.length) return null;
  const factor = pressureUnitDefinition(familyData.pressure_unit).toBar;
  const values = corrected.map((item) => item.corrected);
  const uStandard = mean(corrected.map((item) => item.standard.uncertainty_expanded_bar / item.standard.coverage_factor)) / factor;
  const maxHysteresis = Math.max(...corrected.map((item) => item.standard.hysteresis_bar)) / factor;
  const resolutionBar = Math.abs(finiteOrNull(familyData.standard_configuration?.reference?.capability?.resolution) ?? 0.001);
  const uResolution = (resolutionBar / factor) / SQRT12;
  const repeatability = sampleStandardDeviation(values);
  const uRepeatability = repeatability / Math.sqrt(Math.max(1, values.length));
  const uHysteresis = (maxHysteresis / 2) / SQRT3;
  const averageValue = Math.abs(mean(values) ?? 0);
  const uOther = averageValue * (Math.abs(finiteOrNull(familyData.uncertainty_budget?.other_percent) ?? 0) / 100) / SQRT3;
  const uc = Math.hypot(uStandard, uResolution, uRepeatability, uHysteresis, uOther);
  return {
    mean: mean(values), minimum: Math.min(...values), maximum: Math.max(...values), repeatability,
    u_standard: uStandard, u_resolution: uResolution, u_repeatability: uRepeatability,
    u_hysteresis: uHysteresis, u_other: uOther, uc, k: 2, u_expanded: uc * 2,
  };
}

export function computeCheckValveMetrics(rows = [], session = {}, snapshot = null, block = {}) {
  const familyData = mergeCheckValveData(session.family_data);
  const activeRows = Array.from({ length: familyData.repeat_count }, (_, index) => rows[index] || { trial_no: index + 1 });
  const trialResults = activeRows.map((row, index) => ({
    trial_no: row.trial_no || index + 1,
    applied: correctedPressure(row.applied_pressure, familyData, snapshot),
    reverse_result: row.reverse_result || 'not_performed',
    leak_onset: correctedPressure(row.leak_pressure, familyData, snapshot),
  }));
  const appliedReadings = trialResults.map((trial) => trial.applied).filter(Boolean);
  if (appliedReadings.length !== familyData.repeat_count) return null;
  const applied = uncertainty(appliedReadings, familyData);
  const target = finiteOrNull(familyData.test_pressure);
  if (!applied || target === null || target <= 0) return null;
  const guard = familyData.decision_rule === 'guard_band' ? applied.u_expanded : 0;
  // La presión de ensayo admite una tolerancia declarada: comprobamos que el
  // ensayo se hizo a presión suficiente, no medimos la válvula con ella.
  const pressureTolerancePct = Math.abs(finiteOrNull(familyData.test_pressure_tolerance_percent) ?? 0);
  const pressureThreshold = target * (1 - pressureTolerancePct / 100);
  const pressurePass = applied.minimum - guard >= pressureThreshold;
  const reverseResults = activeRows.map((row) => String(row.reverse_result || 'not_performed'));
  const reversePass = reverseResults.every((value) => value === 'satisfactory');
  const forwardPass = familyData.forward_flow_result === 'satisfactory';
  const failedTrials = activeRows.filter((row) => row.reverse_result === 'fail').map((row) => row.trial_no);
  const leakOnsetReadings = trialResults.map((trial) => trial.leak_onset).filter(Boolean);
  return {
    block_id: block.id || null,
    unit: familyData.pressure_unit,
    target_pressure: target,
    applied,
    applied_readings: appliedReadings,
    leak_onset_readings: leakOnsetReadings,
    trial_results: trialResults,
    pressure_margin: applied.minimum - target,
    pressure_tolerance_percent: pressureTolerancePct || null,
    pressure_threshold: pressureThreshold,
    // pressure_pass = el ENSAYO se hizo a presión suficiente (adecuación del método).
    // valve_pass    = la VÁLVULA cumplió (paso directo + hermeticidad inversa).
    // Separarlos evita leer como "válvula defectuosa" lo que es un ensayo corto.
    pressure_pass: pressurePass,
    forward_pass: forwardPass,
    reverse_pass: reversePass,
    valve_pass: forwardPass && reversePass,
    failed_trials: failedTrials,
    overall_pass: pressurePass && forwardPass && reversePass,
    decision_rule: familyData.decision_rule,
    leak_acceptance: familyData.leak_acceptance,
  };
}

export function validateCheckValveRows(rows = [], familyData = {}, snapshot = null) {
  const merged = mergeCheckValveData(familyData);
  const issues = [];
  if (!(finiteOrNull(merged.test_pressure) > 0)) issues.push('Falta una presión de prueba mayor que cero.');
  if (!snapshot) issues.push('Falta seleccionar un patrón de presión vigente.');
  if (merged.forward_flow_result === 'not_performed') issues.push('Falta registrar la verificación de paso en sentido directo.');
  for (let index = 0; index < merged.repeat_count; index += 1) {
    const row = rows[index] || {};
    const applied = finiteOrNull(row.applied_pressure);
    const leak = finiteOrNull(row.leak_pressure);
    if (applied === null) issues.push(`Falta la presión aplicada del ensayo ${index + 1}.`);
    else if (snapshot && !standardAtPressure(snapshot, pressureToBar(applied, merged.pressure_unit, 'pressure'))) issues.push(`La presión aplicada del ensayo ${index + 1} está fuera del alcance certificado del patrón.`);
    if (!['satisfactory', 'fail'].includes(row.reverse_result)) issues.push(`Falta el resultado de hermeticidad inversa del ensayo ${index + 1}.`);
    if (row.reverse_result === 'fail' && leak === null) issues.push(`Registrá la presión de inicio de fuga del ensayo ${index + 1}.`);
    if (leak !== null && snapshot && !standardAtPressure(snapshot, pressureToBar(leak, merged.pressure_unit, 'pressure'))) issues.push(`La presión de fuga del ensayo ${index + 1} está fuera del alcance certificado del patrón.`);
  }
  const target = pressureToBar(merged.test_pressure, merged.pressure_unit, 'pressure');
  if (snapshot && !standardAtPressure(snapshot, target)) issues.push('La presión de prueba está fuera del alcance certificado del patrón.');
  return [...new Set(issues)];
}
